import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, ShieldAlert, Percent, Landmark, Repeat, Lock, KeyRound, Handshake, Volume2, Maximize2 } from "lucide-react";
import { GlobeSeal } from "../components/GlobeSeal";
import { SealWatermark } from "../components/SealWatermark";

// SEUTRADE FINANCIAL SERVICES — the public front door. Customers engage here to apply for
// financing / refinancing. Admin back-office and the token-gated Marketplace are
// available as secondary buttons in the header.
export default function LandingPage() {
  const nav = useNavigate();
  const videoRef = useRef(null);
  const [activated, setActivated] = useState(false);

  const activateVideo = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.loop = false;
    v.currentTime = 0;
    v.controls = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    setActivated(true);
    const req = v.requestFullscreen || v.webkitEnterFullscreen || v.webkitRequestFullscreen;
    if (req) { try { req.call(v); } catch (e) {} }
  };

  const products = [
    { icon: Percent, title: "Low-Rate Refinance", desc: "Refinance eligible balances at highly competitive rates (as low as 0% for qualifying files*) to help lower your monthly payment. Subject to qualification." },
    { icon: Landmark, title: "Asset Financing", desc: "Finance real estate, vehicles, or equipment with a clean, fully-documented closing package." },
    { icon: Repeat, title: "Debt Swap / Settlement", desc: "Resolve an outstanding obligation and receive creditor-issued satisfaction & lien release." },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex flex-col" data-testid="public-landing-page">
      {/* Top bar */}
      <header className="px-6 sm:px-10 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1C1C16]">
        <div>
          <div className="font-serif text-[22px] tracking-tight leading-none">SEUTRADE FINANCE</div>
          <div className="font-serif text-[11px] tracking-[0.22em] uppercase text-[#B8860B] leading-none mt-1" data-testid="landing-ibo-tag">Financial Services</div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] mt-1">
            Private Lending · Financing &amp; Refinance
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => nav("/about")}
            data-testid="landing-about-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#ECECE4] border border-[#2A2A22] hover:border-[#B8860B] px-4 py-2 rounded-full transition-colors"
          >
            About Us
          </button>
          <button
            onClick={() => nav("/tax-debt-program")}
            data-testid="landing-tax-program-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-4 py-2 rounded-full transition-colors"
          >
            Tax Debt Program
          </button>
          <button
            onClick={() => nav("/financial-crimes-unit")}
            data-testid="landing-fciu-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#E4C97A] border border-[#8A6D1F] hover:border-[#E4C97A] px-4 py-2 rounded-full transition-colors"
          >
            <ShieldAlert size={13} /> Report Fraud
          </button>
          <button
            onClick={() => nav("/private-bank")}
            data-testid="landing-private-banker-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-4 py-2 rounded-full transition-colors"
          >
            <Landmark size={13} /> Register with BISS — Private Banker
          </button>
          <button
            onClick={() => nav("/borrower")}
            data-testid="landing-header-borrower-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-[#0A0A0A] bg-[#B8860B] hover:bg-[#a0760a] px-4 py-2 rounded-full transition-colors"
          >
            Borrower Portal <ArrowRight size={13} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="flex-1">
        <section className="relative overflow-hidden px-6 py-20 sm:py-24 max-w-5xl mx-auto text-center">
          <SealWatermark size={640} opacity={0.05} />
          <div className="relative z-10">
          <GlobeSeal />
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] mb-6">
            <ShieldCheck size={12} className="text-[#B8860B]" /> Private Capital · By Application
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Refinance to <span className="text-[#B8860B]">as low as 0%.<span className="text-[#B8860B] align-super text-xl">*</span></span>
            <br />Keep more every month.
          </h1>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] sm:text-xs tracking-[0.18em] uppercase text-[#C9A227]" data-testid="landing-services-line">
            <span>Low-Rate Refinance</span>
            <span className="text-[#3A3A32]">·</span>
            <span>Debt Consolidation</span>
            <span className="text-[#3A3A32]">·</span>
            <span>Mortgage Assumptions</span>
          </div>
          <p className="text-[#9A9A90] text-base mt-6 max-w-2xl mx-auto leading-relaxed">
            SEUTRADE FINANCIAL SERVICES provides private financing, refinancing, and debt-settlement to qualified
            borrowers. Apply online, e-sign your closing package, and set up automatic monthly payments —
            all in one secure portal.
          </p>
          <div className="mt-6 flex justify-center" data-testid="landing-nocreditcheck-badge">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/10 px-5 py-2.5 text-center">
              <ShieldCheck size={16} className="text-[#B8860B] shrink-0" />
              <span className="text-[11px] sm:text-xs tracking-[0.14em] uppercase font-semibold text-[#E4C97A]">
                No Traditional Credit Check*
              </span>
              <span className="hidden sm:inline text-[#3A3A32]">·</span>
              <span className="hidden sm:inline text-[11px] tracking-[0.10em] uppercase text-[#9A9A90]">
                Flexible qualification for eligible applicants
              </span>
            </div>
          </div>
          <p className="mt-3 text-[10px] leading-relaxed text-[#6C6C64] max-w-2xl mx-auto text-center" data-testid="landing-nocreditcheck-disclaimer">
            *“No traditional credit check” means we may qualify applicants without a hard credit-bureau pull; it is not a
            promise of approval. All financing is subject to borrower qualification and underwriting; rates, terms, and funding
            are not guaranteed. SEUTRADE FINANCIAL SERVICES is a private lender — <strong>not a bank</strong>, not a chartered
            depository institution, and <strong>not FDIC-insured</strong>. Currently available in select U.S. states — contact
            us to confirm availability in your state.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3">
            <button
              onClick={() => nav("/borrower")}
              data-testid="landing-apply-btn"
              className="inline-flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors"
            >
              Apply for Financing <ArrowRight size={15} />
            </button>
            <button
              onClick={() => nav("/borrower")}
              data-testid="landing-signin-btn"
              className="inline-flex items-center gap-2 border border-[#2A2A22] hover:border-[#B8860B] text-[#ECECE4] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors"
            >
              Borrower Sign In
            </button>
            <button
              onClick={() => nav("/tax-debt-program")}
              data-testid="landing-tax-program-cta"
              className="inline-flex items-center gap-2 bg-[#6FD3AE] hover:bg-[#54c097] text-[#06160F] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors"
            >
              Settle IRS Tax Debt <ArrowRight size={15} />
            </button>
            <button
              onClick={() => nav("/financial-crimes-unit")}
              data-testid="landing-fciu-cta"
              className="inline-flex items-center gap-2 border border-[#8A6D1F] hover:border-[#E4C97A] text-[#E4C97A] px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.16em] font-bold transition-colors"
            >
              <ShieldAlert size={15} /> Report Fraud
            </button>
          </div>
          </div>
        </section>

        {/* Marketing video */}
        <section className="px-6 pb-8 max-w-4xl mx-auto" data-testid="landing-video-section">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A]">
              <span className="text-[#B8860B]">▶</span> The Seutrade Story · 30 Seconds
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl mt-2 text-[#ECECE4]">Refinance smarter. Keep more.</h2>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-[#2A2A22] bg-black shadow-[0_20px_60px_rgba(0,0,0,.6)]">
            <video
              ref={videoRef}
              key="seutrade-marketing-v2"
              data-testid="landing-marketing-video"
              className="w-full h-auto block"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls={activated}
              poster="/seutrade_video_poster_v2.jpg"
            >
              <source src="/seutrade_marketing_v2.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {!activated && (
              <button
                type="button"
                onClick={activateVideo}
                data-testid="landing-video-unmute"
                aria-label="Tap for sound and full screen"
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/25 hover:bg-black/10 transition-colors group"
              >
                <span className="w-16 h-16 rounded-full bg-[#B8860B] text-[#0A0A0A] flex items-center justify-center shadow-[0_8px_24px_rgba(0,0,0,.5)] group-hover:scale-105 transition-transform">
                  <Volume2 size={26} />
                </span>
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#ECECE4] bg-black/50 px-4 py-2 rounded-full backdrop-blur">
                  Tap for sound <Maximize2 size={13} className="text-[#C9A227]" /> full screen
                </span>
              </button>
            )}
          </div>
        </section>

        <section className="px-6 pb-24 max-w-5xl mx-auto grid sm:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.title} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-7">
              <div className="w-11 h-11 rounded-md bg-[#B8860B]/10 border border-[#B8860B]/30 flex items-center justify-center mb-5">
                <p.icon size={18} className="text-[#B8860B]" />
              </div>
              <div className="font-serif text-xl mb-2">{p.title}</div>
              <p className="text-[#8A8A8A] text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="px-6 sm:px-10 py-6 border-t border-[#1C1C16] text-center text-[10px] tracking-[0.18em] uppercase text-[#5C5C5C]">
        <div>SEUTRADE FINANCIAL SERVICES · Confidential — All financing subject to application &amp; approval</div>
        <div className="mt-2 max-w-3xl mx-auto text-[9px] leading-relaxed tracking-[0.08em] text-[#4C4C46] normal-case">
          Disclaimer: SEUTRADE FINANCIAL SERVICES is a private lender and is <strong>not a bank</strong>, not a chartered
          depository institution, and <strong>not FDIC-insured</strong>; it is not affiliated with any government agency and
          does not accept deposits. “No traditional credit check” means we may qualify applicants without a hard credit-bureau
          pull and does not guarantee approval. All financing is subject to borrower qualification, underwriting, and program
          eligibility. Rates, terms, approval, and funding are not guaranteed and may vary by transaction and jurisdiction.
          Products may not be available in all states. This material is for informational purposes only and is not an offer or
          commitment to lend.
        </div>
        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <button
            onClick={() => nav("/broker")}
            data-testid="landing-broker-btn"
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[#3A3A32] hover:text-[#C9A227] transition-colors"
          >
            <Handshake size={9} /> Brokers
          </button>
          <button
            onClick={() => nav("/securities")}
            data-testid="landing-securities-btn"
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[#3A3A32] hover:text-[#E4C97A] transition-colors"
          >
            <Landmark size={9} /> Securities Loan
          </button>
          <button
            onClick={() => nav("/market")}
            data-testid="landing-marketplace-btn"
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[#3A3A32] hover:text-[#3DBB8E] transition-colors"
          >
            <KeyRound size={9} /> Marketplace
          </button>
          <button
            onClick={() => nav("/admin/login")}
            data-testid="landing-admin-login-btn"
            className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-[#3A3A32] hover:text-[#B8860B] transition-colors"
          >
            <Lock size={9} /> Admin
          </button>
        </div>
      </footer>
    </div>
  );
}
